import { type AxiosError } from 'axios'
import { makeAutoObservable } from 'mobx'

import { FaqController } from '@App/api'
import { EToastings, type IApiErrorHandler, type ISwapSequencs } from '@App/types/common'
import { type IFaqData, type IAddFaqDto, type IEditFaq, type IFaqFilters } from '@App/types/faqTypes'
import { onHandleResponseNotification, onToasting, onUseOdataQueries, onUseQueries } from '@App/utils'
import { defaultFaqFilters } from '@Config/config'

class Faq {
    filters: IFaqFilters = defaultFaqFilters
    count: number = 0
    faqList: IEditFaq[] | null = null

    constructor() {
        makeAutoObservable(this)
    }

    // actions
    onSetFaqData = (data: IEditFaq[] | null) => {
        if (!data) {
            this.faqList = null
        } else {
            this.faqList = data.sort((a, b) => a.sequence - b.sequence)
        }
    }

    onSetFilters(filters: IFaqFilters) {
        this.filters = filters
    }

    onSetTotalCount(count: number) {
        this.count = count
    }

    // requests
    async onGetFaqData() {
        const client = new FaqController()
        const filters = onUseOdataQueries({
            search: this.filters.search
                ? {
                      operator: 'or',
                      query: [
                          { key: 'header', value: this.filters.search },
                          { key: 'title', value: this.filters.search }
                      ]
                  }
                : null,
            count: true
        })
        try {
            const { data } = await client.onGetFaqData(filters)
            if (data) {
                this.onSetFaqData(data.value.map((item: IFaqData) => ({ ...item, isEdit: false })))
                this.onGetTotalConut()
            }
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        }
    }

    async onGetUnchangedFaqItemById({ id, isEdit = false }: { id: string; isEdit?: boolean }) {
        const client = new FaqController()
        const filters = onUseQueries({ filter: `id eq '${id}'` })

        try {
            const { data } = await client.onGetFaqData(filters)
            if (data && this.faqList) {
                this.onSetFaqData(this.faqList.map((el) => (el.id === id ? { ...data.value[0], isEdit } : el)))
            }
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        }
    }

    async onGetTotalConut() {
        const client = new FaqController()
        const query = onUseQueries({ $count: true })
        try {
            const { data } = await client.onGetFaqData(query)
            if (data) {
                this.onSetTotalCount(data['@odata.count'])
            }
        } catch (e) {
            this.onSetTotalCount(0)
        }
    }

    async onUpdateFaqList(dto: IFaqData) {
        const client = new FaqController()
        try {
            const { status } = await client.onUpdateFaqList(dto)
            this.onSetFaqData(null)
            this.onGetFaqData()
            onHandleResponseNotification(status, `Вопрос успешно обновлен`)
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        }
    }

    async onRemoveFaqById(faqId: string) {
        const client = new FaqController()
        const query = onUseQueries({ faqId })
        try {
            const { status } = await client.onRemoveFaqById(query)
            this.onSetFaqData(null)
            this.onGetFaqData()
            onHandleResponseNotification(status, `Вопрос успешно удален`)
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        }
    }

    async onPostFaq(dto: IAddFaqDto) {
        const client = new FaqController()
        try {
            const { status } = await client.onPostFaq(dto)
            this.onSetFaqData(null)
            this.onGetFaqData()
            onHandleResponseNotification(status, `Вопрос успешно добавлен`)
        } catch (e: any) {
            const error = e as AxiosError<IApiErrorHandler>
            if (error.response?.status === 409) {
                onToasting({
                    type: EToastings.ERROR,
                    message: error.response?.data?.detail || ''
                })
                return
            }
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        }
    }

    async onSwapBySequence(params: IOnSwapBySequenceParams) {
        const { dto } = params
        const client = new FaqController()
        try {
            const { status } = await client.onSwapBySequence(dto)
            if (status === 204) {
                this.onSetFaqData(null)
                this.onGetFaqData()
            }
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        }
    }
}

export default new Faq()

interface IOnSwapBySequenceParams {
    dto: ISwapSequencs
}
